import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BookingService } from "@/services/BookingService";
import type { Booking } from "@/types/Booking";

export type SetRecurrenceExceptionParams = {
  bookingId: string;
  date: string;
  action: "add" | "remove";
  /** Elevated-only: waive the C1 within-24h charge when excluding an occurrence. */
  waive?: boolean;
};

/**
 * React Query mutation hook for adding/removing a single recurrence-instance
 * exception. On remove, the backend returns 409 + a clash payload if the slot
 * is no longer free; consumers should inspect the error (instance of
 * RecurrenceExceptionError) to surface a user-friendly message.
 */
export function useSetRecurrenceException() {
  const bookingService = new BookingService();
  const queryClient = useQueryClient();

  return useMutation<Booking, Error, SetRecurrenceExceptionParams>({
    mutationFn: async (params) => {
      return await bookingService.setRecurrenceException(params.bookingId, params.date, params.action, params.waive);
    },
    onSuccess: (_booking, params) => {
      queryClient.invalidateQueries({ queryKey: ["booking", params.bookingId] });
    },
  });
}
